import React from 'react';
import {maxDepthRecursionWrapper} from './functions';
import {StyledHardRefreshTextLink} from './styles';

const MAX_RECURSION_DEPTH = 5;

export type MarkdownNodeType = 'text' | 'link' | 'linebreak' | 'bold';

export interface MarkdownNode {
  type: MarkdownNodeType;
  value?: string;
  url?: string;
  children?: MarkdownNode[];
}

export type MarkdownPart = string | JSX.Element;

interface ParseResult {
  nodes: MarkdownNode[];
  truncatedNodes?: MarkdownNode[];
}

// Markdown Rule Definition
interface MarkdownRule {
  pattern: RegExp;
  nodeType: MarkdownNodeType;
  hasChildren?: boolean; // If true, parse match[1] recursively for children
}

// Registry of markdown rules (Earlier rules take precedence)
const MARKDOWN_RULES: MarkdownRule[] = [
  {
    pattern: /\r\n|\r|\n/g, // Match Windows (\r\n), old Mac (\r), or Unix (\n) line endings
    nodeType: 'linebreak',
  },
  {
    pattern: /\[([^\]]+)\]\(([^)]+)\)/g,
    nodeType: 'link',
    hasChildren: true,
  },
  {
    pattern: /\*\*(.+?)\*\*/g,
    nodeType: 'bold',
    hasChildren: true,
  },
];

// Calculate visible character count from AST nodes (recursive)
const getVisibleLength = maxDepthRecursionWrapper(
  function getVisibleLengthImpl(nodes: MarkdownNode[]): number {
    let length = 0;

    for (const node of nodes) {
      if (node.type === 'text' && node.value) {
        length += node.value.length;
      } else if (node.children) {
        length += getVisibleLength(node.children);
      }
    }

    return length;
  },
  MAX_RECURSION_DEPTH,
  0,
);

// Parses markdown text into an AST structure
// Optionally truncates to maxCharacters of visible text
export function parseMarkdown(text: string, options?: {maxCharacters?: number}): ParseResult {
  if (!text) return {nodes: []};

  const maxCharacters = options?.maxCharacters;
  const {nodes: fullNodes, length: totalTextLength} = parseRecursive(text);

  if (maxCharacters === undefined || totalTextLength <= maxCharacters) {
    return {nodes: fullNodes};
  }

  const truncatedNodes = truncateNodes(fullNodes, maxCharacters);

  return {nodes: fullNodes, truncatedNodes};
}

// Recursively parse text with sequential left-to-right matching
// Does not support nested tokens of the same character.
const parseRecursive = maxDepthRecursionWrapper(
  function parseRecursiveImpl(text: string): {nodes: MarkdownNode[]; length: number} {
    const nodes: MarkdownNode[] = [];
    let totalTextLength = 0;
    let position = 0;
    let textBuffer = '';

    const flushBuffer = () => {
      if (textBuffer) {
        nodes.push({type: 'text', value: textBuffer});
        totalTextLength += textBuffer.length;
        textBuffer = '';
      }
    };

    while (position < text.length) {
      let matched = false;

      // Try each rule at the current position
      for (const rule of MARKDOWN_RULES) {
        const remainingText = text.substring(position);
        const regex = new RegExp(rule.pattern.source, rule.pattern.flags);
        const match = regex.exec(remainingText);

        // Check if the match is at the current position
        if (match && match.index === 0) {
          flushBuffer();

          if (rule.hasChildren) {
            // Recursively parse children from capture group 1
            const {nodes: children, length} = parseRecursive(match[1]);
            const node: MarkdownNode = {
              type: rule.nodeType,
              children,
            };

            // Add URL if this is a link node from capture group 2
            if (rule.nodeType === 'link') {
              node.url = match[2];
            }

            nodes.push(node);
            totalTextLength += length;
          } else {
            nodes.push({type: rule.nodeType});
          }

          position += match[0].length;
          matched = true;
          break;
        }
      }

      // If no rule matched at this position, add character to buffer
      if (!matched) {
        textBuffer += text[position];
        position += 1;
      }
    }

    flushBuffer();

    return {nodes, length: totalTextLength};
  },
  MAX_RECURSION_DEPTH,
  {nodes: [], length: 0},
);

// Trim whitespace and newlines from the last text node in the AST
export const trimLastTextNode = maxDepthRecursionWrapper(
  function trimLastTextNodeImpl(nodes: MarkdownNode[]): MarkdownNode[] {
    if (nodes.length === 0) return nodes;

    const lastNode = nodes[nodes.length - 1];
    const restOfNodes = nodes.slice(0, -1);

    // Remove trailing linebreak nodes
    if (lastNode.type === 'linebreak') {
      return trimLastTextNode(restOfNodes);
    }

    // If last node is a text node, trim whitespace and newlines from its value
    if (lastNode.type === 'text' && lastNode.value) {
      const trimmedValue = lastNode.value.replace(/[\r\n\s]+$/, '');
      if (trimmedValue === '') {
        // If the text node becomes empty after trimming, remove it and continue trimming
        return trimLastTextNode(restOfNodes);
      }
      return [...restOfNodes, {...lastNode, value: trimmedValue}];
    }

    // If last node has children, recursively trim the last child
    if (lastNode.children) {
      const trimmedChildren = trimLastTextNode(lastNode.children);
      const updatedNode = {...lastNode, children: trimmedChildren};
      return [...restOfNodes, updatedNode];
    }

    return nodes;
  },
  MAX_RECURSION_DEPTH,
  [],
);

const truncateNodes = maxDepthRecursionWrapper(
  function truncateNodesImpl(nodes: MarkdownNode[], maxCharacters: number): MarkdownNode[] {
    const result: MarkdownNode[] = [];
    let charCount = 0;

    for (const node of nodes) {
      const nodeLength = getVisibleLength([node]);

      if (charCount + nodeLength <= maxCharacters) {
        result.push(node);
        charCount += nodeLength;
        continue;
      }

      const remainingChars = maxCharacters - charCount;

      if (remainingChars > 0) {
        if (node.type === 'text' && node.value) {
          const truncatedValue = node.value.slice(0, remainingChars);
          result.push({type: 'text', value: truncatedValue});
          charCount += truncatedValue.length;
        } else if (node.children) {
          const truncatedChildren = truncateNodes(node.children, remainingChars);
          const truncatedNode = {...node, children: truncatedChildren};
          result.push(truncatedNode);
          charCount += getVisibleLength(truncatedChildren);
        }
      }

      // We've hit the maxCharacters so break
      break;
    }

    return result;
  },
  MAX_RECURSION_DEPTH,
  [],
);

// Converts AST nodes to React elements
export const renderMarkdownNodes = maxDepthRecursionWrapper(
  function renderMarkdownNodesImpl(nodes: MarkdownNode[], elementKey: string): JSX.Element {
    const parts: MarkdownPart[] = [];

    nodes.forEach((node, index) => {
      switch (node.type) {
        case 'text':
          if (node.value) {
            parts.push(node.value);
          }
          break;

        case 'linebreak':
          parts.push(<br />);
          break;

        case 'link':
          if (node.children && node.url) {
            // Recursively render children for nested content
            const content = renderMarkdownNodes(node.children, `${elementKey}-${index}`);
            parts.push(
              <StyledHardRefreshTextLink href={node.url}>{content}</StyledHardRefreshTextLink>,
            );
          }
          break;

        case 'bold':
          if (node.children) {
            const content = renderMarkdownNodes(node.children, `${elementKey}-${index}`);
            parts.push(<strong>{content}</strong>);
          }
          break;
      }
    });

    return (
      <>
        {parts.map((part, i) => (
          <React.Fragment key={`markdown-part-${elementKey}-${i}`}>{part}</React.Fragment>
        ))}
      </>
    );
  },
  MAX_RECURSION_DEPTH,
  <></>,
);

export function formatMarkdown(text: string, key: string) {
  const {nodes} = parseMarkdown(text);
  return renderMarkdownNodes(nodes, key);
}
